import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
// material
import { alpha, styled } from '@mui/material/styles';
import { Card, Grid, IconButton, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import moment from 'moment';
import { useTranslation } from '../../../common/LocalizationProvider';
import { getItemWithoutHeaders } from '../../../fetch';
import { pointActions } from '../../../store';

import { selectPointItemsData } from '../../../createSelectorUtils';
// utils

// ----------------------------------------------------------------------

const RootStyle = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  textAlign: 'center',
  padding: theme.spacing(5, 0),
  color: theme.palette.primary.darker,
  backgroundColor: theme.palette.primary.superLight
}));

const IconWrapperStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  width: theme.spacing(5),
  height: theme.spacing(5),
  justifyContent: 'center',
  color: theme.palette.primary.darker,
  backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.darker, 0)} 0%, ${alpha(
    theme.palette.primary.darker,
    0.24
  )} 100%)`
}));

const imageGrade = (totalPoint) => {
  if (totalPoint >= 50000) {
    return '/static/grade/Diamant.png';
  }
  if (totalPoint >= 20000 && totalPoint < 50000) {
    return '/static/grade/Platine.png';
  }

  if (totalPoint >= 10000 && totalPoint < 20000) {
    return '/static/grade/Or.png';
  }

  if (totalPoint >= 5000 && totalPoint < 10000) {
    return '/static/grade/Argent.png';
  }

  if (totalPoint >= 2000 && totalPoint < 5000) {
    return '/static/grade/Bronze.png';
  }

  if (totalPoint >= 1000 && totalPoint < 2000) {
    return '/static/grade/Bois.png';
  }

  return '/static/grade/Grade1.png';
};

const gradeTooltip = (totalPoint) => {
  if (totalPoint >= 50000) {
    return 'Diamant: points >= 50000';
  }
  if (totalPoint >= 20000 && totalPoint < 50000) {
    return 'Platine: 20000 <= points < 50000 ';
  }

  if (totalPoint >= 10000 && totalPoint < 20000) {
    return 'Or: 10000 <= points < 20000';
  }

  if (totalPoint >= 5000 && totalPoint < 10000) {
    return 'Argent: 5000 <= points < 10000';
  }

  if (totalPoint >= 2000 && totalPoint < 5000) {
    return 'Bronze: 2000 <= points < 5000';
  }

  if (totalPoint >= 1000 && totalPoint < 2000) {
    return 'Bois: 1000 <= points < 2000';
  }

  return 'Grade 1: points < 1000';
};

// ----------------------------------------------------------------------

export default function AppPointsInfo() {
  const t = useTranslation();
  const dispatch = useDispatch();
  const hiddenDown = useMediaQuery((theme) => theme.breakpoints.between('xs', 'sm'));

  const point = useSelector(selectPointItemsData);

  const refreshPoint = async () => {
    const response = await getItemWithoutHeaders('session/point');
    if (response.ok) {
      dispatch(pointActions.update(await response.json()));
    }
  };
  return (
    <>
      <Stack
        direction="row"
        flexWrap="wrap-reverse"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ mb: 1 }}
      >
        <IconWrapperStyle>
          <Tooltip title="refresh">
            <IconButton onClick={refreshPoint} component="label">
              <Icon icon="charm:refresh" width={20} height={20} />
            </IconButton>
          </Tooltip>
        </IconWrapperStyle>
      </Stack>
      <RootStyle>
        <Grid container spacing={3}>
          <Grid
            item
            xs={6}
            sm={point.totalHoursInstructeur ? 2 : 2.4}
            position="center"
            alignItems="center"
            sx={{ marginLeft: hiddenDown ? '25%' : 0 }}
          >
            <Tooltip title={gradeTooltip(point.totalPoint)}>
              <img src={imageGrade(point.totalPoint)} alt="login" />
            </Tooltip>
          </Grid>
          {point.firstFlight ? (
            <>
              <Grid
                item
                xs={6}
                sm={point.totalHoursInstructeur ? 2 : 2.4}
                md={point.totalHoursInstructeur ? 2 : 2.4}
                sx={{ mt: 4 }}
              >
                <Typography variant="h3">{point.totalPoint || 0}</Typography>
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  {t('DB_SideBar_TotalPoint')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                sm={point.totalHoursInstructeur ? 2 : 2.4}
                md={point.totalHoursInstructeur ? 2 : 2.4}
                sx={{ mt: 4 }}
              >
                <Typography variant="h3">{`${
                  Math.round(point.totalHoursPilot / (60000 * 60)) || 0
                } h`}</Typography>
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  {t('DB_SideBar_Total_Flight_hours')}
                </Typography>
              </Grid>
              {point.totalHoursInstructeur !== 0 && (
                <Grid item xs={6} sm={2} md={2} sx={{ mt: 4 }}>
                  <Typography variant="h3">{`${
                    Math.round(point.totalHoursInstructeur / (60000 * 60)) || 0
                  } h`}</Typography>
                  <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                    {t('DB_SideBar_Total_Instruction_hours')}
                  </Typography>
                </Grid>
              )}
              <Grid
                item
                xs={6}
                sm={point.totalHoursInstructeur ? 2 : 2.4}
                md={point.totalHoursInstructeur ? 2 : 2.4}
                sx={{ mt: 4 }}
              >
                <Typography variant="h3">
                  {(point.firstFlight && moment(point.firstFlight).format('DD/MM/YYYY')) || ''}
                </Typography>
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  {t('DB_SideBar_First_Flight')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                sm={point.totalHoursInstructeur ? 2 : 2.4}
                md={point.totalHoursInstructeur ? 2 : 2.4}
                sx={{ mt: 4 }}
              >
                <Typography variant="h3">
                  {(point.firstFlight && moment(point.lastFlight).format('DD/MM/YYYY')) || ''}
                </Typography>
                <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
                  {t('DB_SideBar_Last_Flight')}
                </Typography>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body2">{t('DB_SideBar_No_Flight')}</Typography>
            </Grid>
          )}
        </Grid>
      </RootStyle>
    </>
  );
}
